import { useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { api } from '../lib/api';
import { staffSiteUrl, siteByKey } from '../lib/sites';

export default function Login() {
  const { loginAsUser } = useAuth();
  const [mode, setMode] = useState('signin'); // 'signin' | 'reset' | 'reset-sent'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // Show/hide password toggle. Useful on phones where mistypes are
  // common and the masked dots make it hard to tell what went wrong.
  const [showPassword, setShowPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await loginAsUser(email, password);
    } catch (err) {
      setError(err.message || 'Invalid credentials.');
    } finally {
      setLoading(false);
    }
  }

  async function handleResetSubmit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await api.requestPasswordReset(resetEmail.trim());
      setMode('reset-sent');
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  function switchToReset() {
    setError('');
    setResetEmail(email);
    setMode('reset');
  }

  function switchToSignIn() {
    setError('');
    setMode('signin');
  }

  return (
    <div style={styles.root}>
      <div data-login-card style={styles.card}>
        <div style={styles.logo}>
          <img data-login-logo src="/logo-full.png" alt="Silver Service" style={styles.logoImg} />
          <div style={styles.logoSub}>by Illy Colorado</div>
        </div>

        {mode === 'signin' && (
          <>
            <h1 style={styles.title}>Customer Portal</h1>
            <p style={styles.subtitle}>Sign in to place orders and manage your account</p>

            <form onSubmit={handleSubmit} style={styles.form}>
              <div style={styles.field}>
                <label style={styles.label}>Email</label>
                <input
                  style={styles.input}
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                  autoFocus
                />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Password</label>
                <div style={styles.passwordWrap}>
                  <input
                    style={{ ...styles.input, paddingRight: '4.2rem' }}
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Your password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    autoComplete="current-password"
                    required
                  />
                  <button
                    type="button"
                    style={styles.passwordToggle}
                    onClick={() => setShowPassword(s => !s)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    aria-pressed={showPassword}
                    tabIndex={-1}
                  >
                    {showPassword ? 'Hide' : 'Show'}
                  </button>
                </div>
              </div>

              <div style={styles.forgotRow}>
                <button type="button" onClick={switchToReset} style={styles.linkBtn}>
                  Forgot password?
                </button>
              </div>

              {error && (
                <div style={styles.error}>
                  <div style={styles.errorIcon}>!</div>
                  <div>
                    <div style={styles.errorText}>{error}</div>
                    <div style={styles.errorHint}>
                      Please check your credentials, or contact{' '}
                      <a href="mailto:orders@silverservice.com" style={styles.errorLink}>orders@silverservice.com</a> for help.
                    </div>
                  </div>
                </div>
              )}

              <button style={styles.btn} type="submit" disabled={loading}>
                {loading ? 'Signing in...' : 'Sign In'}
              </button>
            </form>

            <div style={styles.divider} />

            <div style={styles.footer}>
              <p>Don't have access? <a href="https://www.silverservice.com/contact-us/" target="_blank" rel="noopener noreferrer" style={styles.link}>Contact us</a></p>
              <p>Silver Service staff? <a href={staffSiteUrl(siteByKey('orders'))} style={styles.link}>See the portal as a customer</a></p>
            </div>
          </>
        )}

        {mode === 'reset' && (
          <>
            <h1 style={styles.title}>Reset Password</h1>
            <p style={styles.subtitle}>
              Enter the email for your portal account. We'll send you a link to set a new password.
            </p>

            <form onSubmit={handleResetSubmit} style={styles.form}>
              <div style={styles.field}>
                <label style={styles.label}>Email</label>
                <input
                  style={styles.input}
                  type="email"
                  placeholder="your@email.com"
                  value={resetEmail}
                  onChange={e => setResetEmail(e.target.value)}
                  required
                  autoFocus
                />
              </div>

              {error && (
                <div style={styles.error}>
                  <div style={styles.errorIcon}>!</div>
                  <div>
                    <div style={styles.errorText}>{error}</div>
                  </div>
                </div>
              )}

              <button style={styles.btn} type="submit" disabled={loading}>
                {loading ? 'Sending...' : 'Send Reset Link'}
              </button>
            </form>

            <div style={styles.divider} />

            <div style={styles.footer}>
              <button type="button" onClick={switchToSignIn} style={styles.linkBtn}>
                ← Back to sign in
              </button>
            </div>
          </>
        )}

        {mode === 'reset-sent' && (
          <>
            <h1 style={styles.title}>Check Your Email</h1>
            <p style={styles.subtitle}>
              If a matching account was found, we've sent a password reset link to your email. The link is valid for 24 hours.
            </p>
            <p style={styles.hint}>
              Didn't get the email? Check your spam folder, or contact{' '}
              <a href="mailto:orders@silverservice.com" style={styles.errorLink}>orders@silverservice.com</a> for help.
            </p>

            <div style={styles.divider} />

            <div style={styles.footer}>
              <button type="button" onClick={switchToSignIn} style={styles.linkBtn}>
                ← Back to sign in
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const styles = {
  root: { minHeight: '100vh', background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem', fontFamily: "'Inter', sans-serif" },
  card: { background: '#fff', border: '1px solid #e0e0e0', borderRadius: 12, padding: '2.5rem', width: '100%', maxWidth: 420, boxShadow: '0 4px 24px rgba(0,0,0,0.06)' },
  logo: { textAlign: 'center', marginBottom: '2rem' },
  logoImg: { height: 260, width: 'auto', marginBottom: '0.75rem' },
  logoSub: { fontSize: '0.7rem', color: 'rgba(0,0,0,0.35)', letterSpacing: '0.12em', textTransform: 'uppercase', marginTop: '0.2rem' },
  title: { fontSize: '1.3rem', fontWeight: 600, color: '#333', margin: '0 0 0.4rem', textAlign: 'center' },
  subtitle: { fontSize: '0.85rem', color: 'rgba(0,0,0,0.45)', textAlign: 'center', margin: '0 0 1.5rem' },
  form: { display: 'flex', flexDirection: 'column', gap: '1.2rem' },
  field: { display: 'flex', flexDirection: 'column', gap: '0.4rem' },
  label: { fontSize: '0.8rem', color: 'rgba(0,0,0,0.5)', fontWeight: 500, letterSpacing: '0.04em' },
  input: { background: '#fff', border: '1px solid #ddd', borderRadius: 6, padding: '0.75rem 1rem', color: '#333', fontSize: '0.95rem', outline: 'none', transition: 'border 0.15s' },
  passwordWrap: { position: 'relative' },
  passwordToggle: {
    position: 'absolute', right: '0.5rem', top: '50%', transform: 'translateY(-50%)',
    background: 'none', border: 'none', color: '#A50B18',
    fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer',
    padding: '0.4rem 0.65rem', fontFamily: 'inherit',
    letterSpacing: '0.02em',
  },
  error: { background: 'rgba(231,76,60,0.06)', border: '1px solid rgba(231,76,60,0.2)', borderRadius: 8, padding: '0.85rem 1rem', display: 'flex', gap: '0.75rem', alignItems: 'flex-start' },
  errorIcon: { background: '#c0392b', color: '#fff', width: 20, height: 20, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: 700, flexShrink: 0, marginTop: 1 },
  errorText: { color: '#c0392b', fontSize: '0.85rem', fontWeight: 500 },
  errorHint: { color: 'rgba(0,0,0,0.45)', fontSize: '0.78rem', marginTop: '0.3rem', lineHeight: 1.4 },
  errorLink: { color: '#A50B18', textDecoration: 'none' },
  btn: { background: '#A50B18', color: '#fff', border: 'none', borderRadius: 6, padding: '0.85rem', fontSize: '0.95rem', fontWeight: 700, cursor: 'pointer', marginTop: '0.5rem', letterSpacing: '0.03em' },
  divider: { height: 1, background: '#e0e0e0', margin: '1.5rem 0' },
  footer: { marginTop: '1.5rem', textAlign: 'center', fontSize: '0.82rem', color: 'rgba(0,0,0,0.4)' },
  link: { color: '#A50B18', textDecoration: 'none' },
  forgotRow: { display: 'flex', justifyContent: 'flex-end', marginTop: '-0.4rem' },
  linkBtn: { background: 'none', border: 'none', color: '#A50B18', fontSize: '0.82rem', cursor: 'pointer', padding: 0, textDecoration: 'none', fontFamily: 'inherit' },
  hint: { fontSize: '0.82rem', color: 'rgba(0,0,0,0.55)', textAlign: 'center', margin: '0.75rem 0 0', lineHeight: 1.5 },
};
